import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState("checking");
    const sessionId = params.get("session_id");

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let tries = 0;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
            } catch { /* keep trying */ }
            if (tries++ < 8) setTimeout(poll, 1500);
            else setState("timeout");
        };
        poll();
    }, [sessionId]);

    return (
        <div className="min-h-screen grid place-items-center bg-brand-bone px-4">
            <div className="bg-white border border-border rounded-2xl p-10 max-w-md text-center" data-testid="payment-result">
                {state === "checking" && <><Loader2 className="h-10 w-10 mx-auto animate-spin text-brand-emerald" /><p className="mt-4 text-muted-foreground">Confirming your payment…</p></>}
                {state === "paid" && <>
                    <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
                    <h1 className="text-2xl font-semibold text-brand-emerald mt-4">Subscription active</h1>
                    <p className="text-muted-foreground mt-2">Thank you — your ProcureEd subscription is now active.</p>
                    <Button className="mt-6 bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" onClick={() => navigate("/app")} data-testid="go-dashboard-button">Go to dashboard</Button>
                </>}
                {(state === "error" || state === "timeout") && <>
                    <XCircle className="h-14 w-14 mx-auto text-rose-500" />
                    <h1 className="text-2xl font-semibold text-brand-emerald mt-4">{state === "timeout" ? "Still processing" : "Payment issue"}</h1>
                    <p className="text-muted-foreground mt-2">{state === "timeout" ? "Your payment is taking longer than usual. Check billing shortly." : "We couldn't confirm this payment."}</p>
                    <Button variant="outline" className="mt-6 rounded-lg" onClick={() => navigate("/app/billing")}>Back to billing</Button>
                </>}
            </div>
        </div>
    );
}
